import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, Mail, Phone, Tag, Plus, X, MessageSquare, Trash2, Loader2, Clock,
} from "lucide-react";
import { toast } from "sonner";
import { CallPanel } from "../components/CallPanel";

export default function ContactDetail() {
  const { contactId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");
  const [newTag, setNewTag] = useState("");

  const load = useCallback(() => {
    api.get(`/contacts/${contactId}`).then(({ data }) => setData(data)).catch(() => navigate("/contacts"));
  }, [contactId, navigate]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  const c = data.contact;

  const addNote = async () => {
    if (!note.trim()) return;
    await api.post(`/contacts/${contactId}/notes`, { text: note });
    setNote(""); toast.success("Note added"); load();
  };

  const addTag = async () => {
    if (!newTag.trim()) return;
    const tags = [...(c.tags || []), newTag.trim()];
    await api.put(`/contacts/${contactId}`, { tags });
    setNewTag(""); load();
  };

  const removeTag = async (t) => {
    await api.put(`/contacts/${contactId}`, { tags: c.tags.filter((x) => x !== t) });
    load();
  };

  const del = async () => {
    await api.delete(`/contacts/${contactId}`);
    toast.success("Contact deleted"); navigate("/contacts");
  };

  return (
    <div className="space-y-6 animate-fade-up max-w-5xl">
      <button onClick={() => navigate("/contacts")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-contacts">
        <ArrowLeft className="w-4 h-4" /> Contacts
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-6">
          <Card className="p-6 bg-card border-border/70">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-primary/15 text-primary flex items-center justify-center text-xl font-display font-bold">
                {c.name[0]?.toUpperCase()}
              </div>
              <div className="min-w-0">
                <h1 className="text-xl font-display font-bold truncate">{c.name}</h1>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">{c.source}</p>
              </div>
            </div>
            <div className="mt-5 space-y-2.5 text-sm">
              {c.email && <div className="flex items-center gap-2.5 text-muted-foreground"><Mail className="w-4 h-4" />{c.email}</div>}
              {c.phone && <div className="flex items-center gap-2.5 text-muted-foreground"><Phone className="w-4 h-4" />{c.phone}</div>}
            </div>
            <div className="mt-5 flex gap-2">
              <Button size="sm" variant="secondary" className="gap-1.5 flex-1" onClick={() => navigate("/conversations")} data-testid="open-conversation-button">
                <MessageSquare className="w-4 h-4" /> Message
              </Button>
              <Button size="sm" variant="outline" onClick={del} className="text-destructive hover:text-destructive" data-testid="delete-contact-button">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </Card>

          <CallPanel contact={c} onLogged={load} />

          <Card className="p-6 bg-card border-border/70">
            <h3 className="font-display font-semibold flex items-center gap-2 mb-4"><Tag className="w-4 h-4" /> Tags</h3>
            <div className="flex flex-wrap gap-2 mb-3">
              {(c.tags || []).map((t) => (
                <Badge key={t} variant="secondary" className="gap-1 pr-1" data-testid={`tag-${t}`}>
                  {t}
                  <button onClick={() => removeTag(t)} className="hover:text-destructive"><X className="w-3 h-3" /></button>
                </Badge>
              ))}
              {(!c.tags || c.tags.length === 0) && <span className="text-sm text-muted-foreground">No tags</span>}
            </div>
            <div className="flex gap-2">
              <Input value={newTag} onChange={(e) => setNewTag(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addTag()}
                placeholder="Add tag…" className="h-9" data-testid="add-tag-input" />
              <Button size="sm" onClick={addTag} data-testid="add-tag-button"><Plus className="w-4 h-4" /></Button>
            </div>
          </Card>

          {Object.keys(c.custom_fields || {}).length > 0 && (
            <Card className="p-6 bg-card border-border/70">
              <h3 className="font-display font-semibold mb-4">Custom fields</h3>
              <div className="space-y-2 text-sm">
                {Object.entries(c.custom_fields).map(([k, v]) => (
                  <div key={k} className="flex justify-between"><span className="text-muted-foreground capitalize">{k}</span><span className="font-mono">{String(v)}</span></div>
                ))}
              </div>
            </Card>
          )}
        </div>

        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6 bg-card border-border/70">
            <h3 className="font-display font-semibold mb-4">Add a note</h3>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Log a call, meeting, or reminder…"
              className="min-h-[90px]" data-testid="note-input" />
            <div className="flex justify-end mt-3">
              <Button size="sm" onClick={addNote} disabled={!note.trim()} data-testid="save-note-button">Save note</Button>
            </div>
          </Card>

          <Card className="p-6 bg-card border-border/70">
            <h3 className="font-display font-semibold mb-5 flex items-center gap-2"><Clock className="w-4 h-4" /> Activity timeline</h3>
            <div className="space-y-4">
              {data.notes.map((n) => (
                <div key={n.id} className="flex gap-3" data-testid={`note-${n.id}`}>
                  <div className="w-2 h-2 rounded-full bg-primary mt-2 shrink-0" />
                  <div>
                    <p className="text-sm">{n.text}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{n.author} · {new Date(n.created_at).toLocaleString()}</p>
                  </div>
                </div>
              ))}
              {data.activities.map((a) => (
                <div key={a.id} className="flex gap-3">
                  <div className="w-2 h-2 rounded-full bg-muted-foreground/50 mt-2 shrink-0" />
                  <div>
                    <p className="text-sm text-muted-foreground">{a.text}</p>
                    <p className="text-xs text-muted-foreground/70 mt-0.5">{new Date(a.created_at).toLocaleString()}</p>
                  </div>
                </div>
              ))}
              {data.notes.length === 0 && data.activities.length === 0 && (
                <p className="text-sm text-muted-foreground">No activity yet.</p>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
